'use client';

import {
  useState, useCallback, useRef, useEffect, useMemo, KeyboardEvent,
} from 'react';
import { useTranslation } from 'next-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '@components/common';
import { CandidateSearchFilters } from '@components/contexts/SidebarNavigationContext';
import { useSwrNextJobs } from '@hooks/useSwrApi/endpoints/next-jobs';
import { slugify } from '@utils/strings/slugify';
import { DetectedFiltersChips } from './DetectedFiltersChips';

interface CandidateJobSearchFormProps {
  onSearch: (filters: CandidateSearchFilters) => void;
  initialFilters?: CandidateSearchFilters;
}

const MAX_ROWS = 2;

// Compare slugified forms because URL locations come back lowercased and de-diacriticised
// (`bucuresti`) while the saved alert holds the canonical name (`București`).
const sameLocations = (a?: string[] | null, b?: string[] | null): boolean => {
  const left = (a ?? []).map(slugify).sort();
  const right = (b ?? []).map(slugify).sort();
  if (left.length !== right.length) return false;
  return left.every((value, i) => value === right[i]);
};

export const CandidateJobSearchForm = ({ onSearch, initialFilters }: CandidateJobSearchFormProps) => {
  const { t } = useTranslation('common');

  // Look up the saved alert as a fallback source for `originalKeyword`/`decomposedFilters`.
  // When the form was opened via the sidebar's edit click, these arrive in `initialFilters`.
  // When the user landed directly on the `?edit=1` URL (deep link / refresh / shared link),
  // only the URL-derived `keyword`/`locations`/`salary` are present, so we hydrate the rest
  // by matching the saved alert from the next-jobs response.
  const { data: nextJobs } = useSwrNextJobs({});
  const fallbackAlert = useMemo(() => {
    if (initialFilters?.originalKeyword && initialFilters.decomposedFilters) return undefined;
    if (!nextJobs || !initialFilters?.keyword) return undefined;
    const targetTitle = slugify(initialFilters.keyword);
    return nextJobs.find((item) => (
      item.sourceType === 'job_search_alert'
      && slugify(item.jobTitle) === targetTitle
      && (item.salary ?? undefined) === initialFilters.salary
      && sameLocations(item.locations, initialFilters.locations)
    ));
  }, [nextJobs, initialFilters]);

  const effectiveOriginalKeyword = initialFilters?.originalKeyword ?? fallbackAlert?.originalKeyword ?? undefined;
  const effectiveDecomposedFilters = initialFilters?.decomposedFilters ?? fallbackAlert?.decomposedFilters;

  const initialKeyword = effectiveOriginalKeyword ?? initialFilters?.keyword ?? '';
  const [keyword, setKeyword] = useState(initialKeyword);
  const userEditedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // When the fallback alert arrives asynchronously (after initial mount), update the textarea
  // unless the user has already started typing — we don't want to clobber their input.
  useEffect(() => {
    if (userEditedRef.current) return;
    if (!effectiveOriginalKeyword) return;
    if (keyword === effectiveOriginalKeyword) return;
    setKeyword(effectiveOriginalKeyword);
  }, [effectiveOriginalKeyword, keyword]);

  const decomposedFilters = effectiveDecomposedFilters;
  const hasSelection = !!keyword.trim();

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 24;
    const paddingY = parseFloat(window.getComputedStyle(el).paddingTop)
      + parseFloat(window.getComputedStyle(el).paddingBottom);
    const maxHeight = lineHeight * MAX_ROWS + paddingY;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [keyword, resizeTextarea]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!hasSelection) return;
    onSearch({ keyword: keyword.trim() });
  }, [keyword, hasSelection, onSearch]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (hasSelection) onSearch({ keyword: keyword.trim() });
    }
  }, [keyword, hasSelection, onSearch]);

  const chipsVisible = useMemo(() => !!decomposedFilters && Object.keys(decomposedFilters).length > 0, [decomposedFilters]);

  return (
    <div className="flex grow flex-col items-center justify-end px-4 pb-16 pt-8 md:pb-10">
      <div className="flex w-full max-w-3xl flex-col gap-4">
        <form id="candidate-job-search-form" onSubmit={handleSubmit} className="flex flex-col gap-2">
          <label htmlFor="candidate_search_keyword" className="block pl-1 text-base font-semibold text-ink-medium">
            {t('extended.keyword.label')}
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-ink-medium" />
            <textarea
              ref={textareaRef}
              id="candidate_search_keyword"
              name="candidate_search_keyword"
              rows={1}
              value={keyword}
              onChange={(e) => { userEditedRef.current = true; setKeyword(e.target.value); }}
              onKeyDown={handleKeyDown}
              placeholder={t('extended.keyword.placeholder')}
              className="block w-full resize-none rounded-2xl border border-input/60 bg-surface py-4 pl-12 pr-20 text-lg leading-7 text-ink shadow-xs placeholder:text-ink-light focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/20"
            />
            <Button
              type="submit"
              form="candidate-job-search-form"
              styling="outline"
              color="light"
              rounding="none"
              aria-label={t('mc.search.job')}
              title={t('mc.search.job')}
              className="!absolute !right-2 !top-1/2 !-translate-y-1/2 !h-12 !w-12 shrink-0 !rounded-xl bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary"
            >
              <MagnifyingGlassIcon className="size-6 text-white" />
            </Button>
          </div>
        </form>

        {chipsVisible && decomposedFilters && (
          <DetectedFiltersChips decomposedFilters={decomposedFilters} />
        )}
      </div>
    </div>
  );
};
